/**
 * 환경설정 > 기본환경 (2026-09-05) — 메일 읽기 설정 · 메일 쓰기 설정.
 *
 * 정본 **P2-1 설정 화면** 의 절·줄·저장바를 그대로 쓴다(`styles/settings-screen.css`).
 *
 * ★★ **저장바가 있는 화면이라 켬/끔은 `Checkbox` 다**(정본 B7 — Switch 는 즉시 반영).
 *    한 화면은 한 가지 반영 시점만 갖는다.
 * ★★ **밀도만은 저장을 안 기다린다.** 목록 위 View 단추와 같은 값이라, 저장하는 순간
 *    기기 사본(mailView)도 함께 고친다 — 어느 쪽에서 바꿔도 함께 갱신된다.
 * ★  묶음에 `fieldset`+`legend` 대신 `role="radiogroup"`+`aria-labelledby` 를 쓴다.
 *    라벨이 **왼쪽 칸**에 서는 2열 배치라 `legend` 를 격자 칸으로 앉히면 브라우저마다 다르게
 *    그려진다. 스크린리더가 「무엇을 고르는 중인지」를 읽어 주는 것은 그대로다.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { getMySettings, patchMySettings, type UserSettingsDto } from '../../api/users';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { Input } from '../../components/shared/Input';
import { Radio, PreviewRadio } from '../../components/shared/Radio';
import { Select } from '../../components/shared/Select';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { invalidateMailPrefs } from './mailPrefs';
import { loadMailView, saveMailView } from './mailView';
import {
  MAIL_BASIC_DEFAULTS, MAIL_ENCODINGS, MAIL_FIRST_FOLDERS, MAIL_PAGE_SIZES, MAIL_UNSAFE_TAGS,
  mailBasicDirty, mailBasicFromDto, mailBasicPatch,
  type MailBasicSettings, type MailFirstFolder,
} from './mailSettings';
import { MailVcardModal } from './MailVcardModal';
import { DensityFigure } from './MailDensityFigure';

/** 저장바가 말하는 것 — 아무 말도 안 하면 「안 됐다」로 읽힌다(정본 P2). */
type FootState = null | 'saving' | 'saved' | 'failed' | 'legacy' | 'load-failed';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 첫 화면 메일함 이름 — 좌측 레일이 쓰는 것과 **같은 말**이어야 한다. */
const FIRST_FOLDER_LABEL: Record<MailFirstFolder, MailI18nKey> = {
  inbox: 'mail.folder.inbox', star: 'mail.folder.star',
  sent: 'mail.folder.sent', draft: 'mail.folder.draft',
};

export function MailSettingsBasic() {
  const [base, setBase] = useState<MailBasicSettings>(MAIL_BASIC_DEFAULTS);
  const [draft, setDraft] = useState<MailBasicSettings>(MAIL_BASIC_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [foot, setFoot] = useState<FootState>(null);
  const [replyErr, setReplyErr] = useState(false);
  const [vcardOpen, setVcardOpen] = useState(false);

  const uid = useId();
  const id = (k: string) => `${uid}-${k}`;

  useEffect(() => {
    let cancelled = false;
    getMySettings()
      .then((s) => {
        if (cancelled) return;
        const v = mailBasicFromDto(s);
        setBase(v);
        setDraft(v);
      })
      .catch(() => { if (!cancelled) setFoot('load-failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = useCallback(<K extends keyof MailBasicSettings>(k: K, v: MailBasicSettings[K]) => {
    setDraft((prev) => ({ ...prev, [k]: v }));
    // 무언가 고치는 순간 「저장됨」을 거둔다 — 안 그러면 고친 뒤에도 저장됐다고 적혀 있다.
    setFoot((f) => (f === 'saved' ? null : f));
  }, []);

  const dirty = useMemo(() => mailBasicDirty(draft, base), [draft, base]);

  const save = useCallback(async () => {
    const patch = mailBasicPatch(draft, base);
    if (Object.keys(patch).length === 0) return;
    if (draft.replyTo.trim() !== '' && !EMAIL_RE.test(draft.replyTo.trim())) {
      setReplyErr(true);
      return;
    }
    setFoot('saving');
    try {
      const next: UserSettingsDto = await patchMySettings(patch);
      const saved = mailBasicFromDto(next);
      setBase(saved);
      setDraft(saved);
      // 메일 화면 세 곳이 나눠 쓰는 모듈 캐시 — 다음에 읽을 때 새 값을 가져오게 한다.
      invalidateMailPrefs();
      /* 밀도는 기기에도 사본을 둔다 — 목록이 뜨자마자 필요한 값이라, 서버 응답을 기다리면
         좁게 그렸다가 넓어지는 것이 눈에 보인다. 목록 위 View 단추가 이 사본을 읽는다. */
      saveMailView({ ...loadMailView(), density: saved.listDensity });
      setFoot('saved');
    } catch (err) {
      // 이 키들을 모르는 서버는 PATCH 스키마가 닫혀 있어 400 을 낸다 — 사실만 알린다.
      const status = (err as { response?: { status?: number } }).response?.status;
      setFoot(status === 400 ? 'legacy' : 'failed');
    }
  }, [base, draft]);

  const revert = useCallback(() => {
    setDraft(base);
    setReplyErr(false);
    setFoot(null);
  }, [base]);

  if (loading) {
    return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;
  }

  return (
    <>
      <div className="wc-set__body">
        {/* ══ 메일 읽기 설정 ══ */}
        <section className="wc-set__sec" aria-labelledby={id('read')}>
          <h2 className="wc-set__sech" id={id('read')}>{tMail('mail.settings.sec.read')}</h2>
          <div className="wc-set__rows">

            {/* ★ 아래 넷은 **개인설정 > 메일**에 있던 것을 옮겨 온 것이다(2026-09-06 사용자 지시).
                값·서버 라우트는 그대로이고 고치는 자리만 하나로 모았다. */}
            <div className="wc-set__row">
              <span className="wc-set__label" id={id('ff')}>{tMail('mail.settings.first_folder')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('ff')}>
                  {MAIL_FIRST_FOLDERS.map((f) => (
                    <Radio
                      key={f}
                      name={id('ff-g')}
                      checked={draft.firstFolder === f}
                      onChange={() => set('firstFolder', f)}
                      label={tMail(FIRST_FOLDER_LABEL[f])}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.page_size')}</span>
              <div className="wc-set__ctl">
                <Select
                  className="wc-set__w1"
                  ariaLabel={tMail('mail.settings.page_size')}
                  value={String(draft.pageSize)}
                  onChange={(v) => set('pageSize', Number(v) as MailBasicSettings['pageSize'])}
                  options={MAIL_PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
                />
                <p className="wc-set__hint">{tMail('mail.settings.page_size_hint')}</p>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.simplify')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.simplifySubject}
                  onChange={(e) => set('simplifySubject', e.target.checked)}
                  label={tMail('mail.settings.simplify_label')}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('addr')}>{tMail('mail.settings.addr_display')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('addr')}>
                  <Radio
                    name={id('addr-g')}
                    checked={draft.addrDisplay === 'NAME'}
                    onChange={() => set('addrDisplay', 'NAME')}
                    label={tMail('mail.settings.addr_name')}
                  />
                  <Radio
                    name={id('addr-g')}
                    checked={draft.addrDisplay === 'NAME_EMAIL'}
                    onChange={() => set('addrDisplay', 'NAME_EMAIL')}
                    label={tMail('mail.settings.addr_name_email')}
                  />
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.images')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.blockImages}
                  onChange={(e) => set('blockImages', e.target.checked)}
                  label={tMail('mail.settings.images_label')}
                />
              </div>
            </div>

            {/* ★ 잠근 칸이다 — 감추지 않고 왜 못 바꾸는지 적는다(정본 P3).
                끄면 본문에 심어 둔 코드가 그대로 돌아 저장형 XSS 가 열린다. */}
            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.unsafe')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked
                  disabled
                  readOnly
                  title={tMail('mail.settings.unsafe_hint')}
                  label={tMail('mail.settings.unsafe_label')}
                />
                <p className="wc-set__hint">
                  {tMail('mail.settings.unsafe_hint')}
                  <span className="mailset__tags"> {MAIL_UNSAFE_TAGS.join(' · ')}</span>
                </p>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.origin')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.showOrigin}
                  onChange={(e) => set('showOrigin', e.target.checked)}
                  label={tMail('mail.settings.origin_label')}
                />
                <p className="wc-set__hint">{tMail('mail.settings.origin_hint')}</p>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.search_scope')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.searchAll}
                  onChange={(e) => set('searchAll', e.target.checked)}
                  label={tMail('mail.settings.search_scope_label')}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.list_screen')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.listShowCc}
                  onChange={(e) => set('listShowCc', e.target.checked)}
                  label={tMail('mail.settings.list_cc_label')}
                />
              </div>
            </div>

            {/* 밀도 — 정본 B6-2 미리보기 라디오의 첫 프로덕션 사용. */}
            <div className="wc-set__row">
              <span className="wc-set__label" id={id('den')}>{tMail('mail.settings.density')}</span>
              <div className="wc-set__ctl">
                <div className="mailset__density" role="radiogroup" aria-labelledby={id('den')}>
                  <PreviewRadio
                    name={id('den-g')}
                    checked={draft.listDensity === 'NARROW'}
                    onChange={() => set('listDensity', 'NARROW')}
                    figure={<DensityFigure density="NARROW" />}
                    title={tMail('mail.density.narrow')}
                    description={tMail('mail.density.narrow_desc')}
                  />
                  <PreviewRadio
                    name={id('den-g')}
                    checked={draft.listDensity === 'WIDE'}
                    onChange={() => set('listDensity', 'WIDE')}
                    figure={<DensityFigure density="WIDE" />}
                    title={tMail('mail.density.wide')}
                    description={tMail('mail.density.wide_desc')}
                  />
                </div>
                <p className="wc-set__hint">{tMail('mail.settings.density_hint')}</p>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" aria-hidden />
              <p className="wc-set__hint">{tMail('mail.settings.pending_read')}</p>
            </div>
          </div>
        </section>

        {/* ══ 메일 쓰기 설정 ══ */}
        <section className="wc-set__sec" aria-labelledby={id('write')}>
          <h2 className="wc-set__sech" id={id('write')}>{tMail('mail.settings.sec.write')}</h2>
          <div className="wc-set__rows">

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('rcp')}>{tMail('mail.settings.recipient_input')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('rcp')}>
                  {([
                    ['AUTOCOMPLETE', 'mail.settings.recipient_auto'],
                    ['NAME_ONLY', 'mail.settings.recipient_name'],
                    ['SEARCH', 'mail.settings.recipient_search'],
                  ] as const).map(([v, k]) => (
                    <Radio
                      key={v}
                      name={id('rcp-g')}
                      checked={draft.recipientInput === v}
                      onChange={() => set('recipientInput', v)}
                      label={tMail(k)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('cfm')}>{tMail('mail.settings.send_confirm')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('cfm')}>
                  <Radio
                    name={id('cfm-g')}
                    checked={draft.sendConfirm}
                    onChange={() => set('sendConfirm', true)}
                    label={tMail('mail.settings.send_confirm_on')}
                  />
                  <Radio
                    name={id('cfm-g')}
                    checked={!draft.sendConfirm}
                    onChange={() => set('sendConfirm', false)}
                    label={tMail('mail.settings.send_confirm_off')}
                  />
                </div>
                <p className="wc-set__hint">{tMail('mail.settings.send_confirm_hint')}</p>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('snt')}>{tMail('mail.settings.save_sent')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('snt')}>
                  <Radio
                    name={id('snt-g')}
                    checked={draft.saveSent}
                    onChange={() => set('saveSent', true)}
                    label={tMail('mail.settings.save_sent_on')}
                  />
                  <Radio
                    name={id('snt-g')}
                    checked={!draft.saveSent}
                    onChange={() => set('saveSent', false)}
                    label={tMail('mail.settings.save_sent_off')}
                  />
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('rcpt')}>{tMail('mail.settings.receipt')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('rcpt')}>
                  <Radio
                    name={id('rcpt-g')}
                    checked={draft.readReceipt}
                    onChange={() => set('readReceipt', true)}
                    label={tMail('mail.settings.use_on')}
                  />
                  <Radio
                    name={id('rcpt-g')}
                    checked={!draft.readReceipt}
                    onChange={() => set('readReceipt', false)}
                    label={tMail('mail.settings.use_off')}
                  />
                </div>
              </div>
            </div>

            {/*
              편집기 — **새 칸이 아니다.** migration 110 의 `mailComposeMode` 가 곧 이것이다.
              ★ 고를 수 있는 것은 html · text 둘이다(사용자 지정). 다만 예전에 markdown 을
                골라 둔 사람에게는 그 칸도 함께 보인다 — 목록에 없는 값을 지금 값으로 두면
                고르기가 「비어 있는」 것처럼 보이고, 다른 것을 누르는 순간 조용히 바뀐다.
            */}
            <div className="wc-set__row">
              <span className="wc-set__label" id={id('edt')}>{tMail('mail.settings.editor')}</span>
              <div className="wc-set__ctl">
                <Select
                  className="wc-set__w1"
                  ariaLabel={tMail('mail.settings.editor')}
                  value={draft.composeMode}
                  onChange={(v) => set('composeMode', v as MailBasicSettings['composeMode'])}
                  options={[
                    { value: 'HTML', label: tMail('mail.settings.editor_html') },
                    { value: 'TEXT', label: tMail('mail.settings.editor_text') },
                    ...(draft.composeMode === 'MARKDOWN'
                      ? [{ value: 'MARKDOWN', label: tMail('mail.settings.editor_md') }]
                      : []),
                  ]}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.quote')}</span>
              <div className="wc-set__ctl">
                <Checkbox
                  checked={draft.quoteDivider}
                  onChange={(e) => set('quoteDivider', e.target.checked)}
                  label={tMail('mail.settings.quote_label')}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('cw')}>{tMail('mail.settings.compose_window')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('cw')}>
                  <Radio
                    name={id('cw-g')}
                    checked={draft.composeWindow === 'PAGE'}
                    onChange={() => set('composeWindow', 'PAGE')}
                    label={tMail('mail.settings.compose_page')}
                  />
                  <Radio
                    name={id('cw-g')}
                    checked={draft.composeWindow === 'POPUP'}
                    onChange={() => set('composeWindow', 'POPUP')}
                    label={tMail('mail.settings.compose_popup')}
                  />
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.encoding')}</span>
              <div className="wc-set__ctl">
                <Select
                  className="wc-set__w2"
                  ariaLabel={tMail('mail.settings.encoding')}
                  value={draft.encoding}
                  onChange={(v) => set('encoding', v as MailBasicSettings['encoding'])}
                  options={MAIL_ENCODINGS.map((e) => ({ value: e.id, label: e.label }))}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id={id('dft')}>{tMail('mail.settings.draft_alert')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline" role="radiogroup" aria-labelledby={id('dft')}>
                  <Radio
                    name={id('dft-g')}
                    checked={draft.draftAlert}
                    onChange={() => set('draftAlert', true)}
                    label={tMail('mail.settings.use_on')}
                  />
                  <Radio
                    name={id('dft-g')}
                    checked={!draft.draftAlert}
                    onChange={() => set('draftAlert', false)}
                    label={tMail('mail.settings.use_off')}
                  />
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <label className="wc-set__label" htmlFor={id('snd')}>{tMail('mail.settings.sender_name')}</label>
              <div className="wc-set__ctl">
                <Input
                  id={id('snd')}
                  className="wc-set__w2"
                  value={draft.senderName}
                  maxLength={100}
                  placeholder={tMail('mail.settings.sender_name_ph')}
                  onChange={(e) => set('senderName', e.target.value)}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <label className="wc-set__label" htmlFor={id('rto')}>{tMail('mail.settings.reply_to')}</label>
              <div className="wc-set__ctl">
                <Input
                  id={id('rto')}
                  className="wc-set__w3"
                  type="email"
                  value={draft.replyTo}
                  maxLength={320}
                  invalid={replyErr}
                  aria-describedby={replyErr ? id('rto-err') : undefined}
                  placeholder={tMail('mail.settings.reply_to_ph')}
                  onChange={(e) => { set('replyTo', e.target.value); if (replyErr) setReplyErr(false); }}
                  /* 치는 동안에는 검사하지 않는다 — 타이핑 중 빨개지면 방해다(정본 P2). */
                  onBlur={(e) => setReplyErr(e.target.value.trim() !== '' && !EMAIL_RE.test(e.target.value.trim()))}
                />
                {replyErr && (
                  <p className="wc-set__hint mailset__err" id={id('rto-err')} role="alert">
                    {tMail('mail.settings.reply_to_err')}
                  </p>
                )}
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.settings.vcard')}</span>
              <div className="wc-set__ctl">
                <div className="wc-set__inline">
                  <Checkbox
                    checked={draft.vcardAttach}
                    onChange={(e) => set('vcardAttach', e.target.checked)}
                    label={tMail('mail.settings.vcard_label')}
                  />
                  <Button variant="secondary" size="sm" onClick={() => setVcardOpen(true)}>
                    {tMail('mail.settings.vcard_manage')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" aria-hidden />
              <p className="wc-set__hint">{tMail('mail.settings.pending')}</p>
            </div>
          </div>
        </section>
      </div>

      {/* ══ 저장바 — 바뀐 것이 없으면 잠긴다 ══ */}
      <div className="wc-set__foot">
        <p
          className={`wc-set__footmsg${foot === 'saved' ? ' is-ok' : ''}${foot === 'failed' || foot === 'legacy' || foot === 'load-failed' ? ' is-bad' : ''}`}
          role={foot === 'failed' || foot === 'legacy' ? 'alert' : 'status'}
        >
          {foot === 'saving' ? tMail('mail.settings.saving')
            : foot === 'saved' ? tMail('mail.settings.saved')
              : foot === 'failed' ? tMail('mail.settings.save_failed')
                : foot === 'legacy' ? tMail('mail.settings.legacy')
                  : foot === 'load-failed' ? tMail('mail.settings.load_failed')
                    : ''}
        </p>
        <Button variant="secondary" onClick={revert} disabled={!dirty || foot === 'saving'}>
          {tMail('mail.settings.revert')}
        </Button>
        <Button
          variant="primary"
          onClick={() => { void save(); }}
          disabled={!dirty}
          loading={foot === 'saving'}
        >
          {tMail('mail.settings.save')}
        </Button>
      </div>

      {vcardOpen && (
        <MailVcardModal
          value={draft.vcard}
          onClose={() => setVcardOpen(false)}
          onDone={(v) => { set('vcard', v); setVcardOpen(false); }}
        />
      )}
    </>
  );
}
